import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, VideoCamera, ArrowClockwise, X } from '@phosphor-icons/react';
import { QRCodeSVG } from 'qrcode.react';
import { EventDropLogo } from '../components/EventDropLogo';
import { getMediaUrl } from '../lib/media';
import { useEventRecovery, useEventSession } from '../context/EventSessionProvider';
import ReconnectingScreen from '../components/ReconnectingScreen';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VOICE = {};
const preload = (n) => { if (!VOICE[n]) { VOICE[n] = new Audio(`${API}/voice/${n}.mp3`); VOICE[n].load(); } };
const say = (n) => { try { const a = VOICE[n] || new Audio(`${API}/voice/${n}.mp3`); a.currentTime = 0; a.play().catch(() => {}); } catch {} };
['get-ready','three','two','one','one-more','last-one','smile','creating-qr','downloading','thank-you'].forEach(preload);

let ac = null;
const shutter = () => { try { if (!ac) ac = new (window.AudioContext || window.webkitAudioContext)(); const o=ac.createOscillator(),g=ac.createGain(); o.connect(g);g.connect(ac.destination); o.frequency.value=1200;g.gain.value=0.1;o.start();g.gain.exponentialRampToValueAtTime(0.001,ac.currentTime+0.06);o.stop(ac.currentTime+0.06); } catch {} };

const BoothPage = () => {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [scr, setScr] = useState('attract');
  const [mode, setMode] = useState('photo');
  const [cd, setCd] = useState(3);
  const [vt, setVt] = useState(0);
  const [pics, setPics] = useState([]);
  const [vidUrl, setVidUrl] = useState(null);
  const [vidBlob, setVidBlob] = useState(null);
  const [bi, setBi] = useState(0);
  const [upl, setUpl] = useState(false);
  const [mom, setMom] = useState(0);
  const [sls, setSls] = useState([]);
  const [si, setSi] = useState(0);
  const [fl, setFl] = useState(false);
  const [cam, setCam] = useState(false);
  const [qr, setQr] = useState(null);
  const [comp, setComp] = useState(null);
  const [cams, setCams] = useState([]);
  const [selCam, setSelCam] = useState('');
  const [admin, setAdmin] = useState(false);
  const [taps, setTaps] = useState(0);
  const [bg, setBg] = useState('#15161B');
  const [tpl, setTpl] = useState('strip');
  const [betweenText, setBetweenText] = useState('');
  const [photoOrientation, setPhotoOrientation] = useState('landscape');
  const [templateConfig, setTemplateConfig] = useState(null); // from template config
  const templateConfigRef = useRef(null);
  useEffect(() => { templateConfigRef.current = templateConfig; }, [templateConfig]);

  const vRef = useRef(null);
  const c1 = useRef(null);
  const c2 = useRef(null);
  const strm = useRef(null);
  const rec = useRef(null);
  const chnk = useRef([]);
  const tmr = useRef(null);
  const cdR = useRef(null);
  const rstR = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        setEv(r.data);
        if (r.data.booth_bg_color) setBg(r.data.booth_bg_color);
        if (r.data.booth_template_style) setTpl(r.data.booth_template_style);
        // Load template config for photo orientation + compositing
        try {
          const cfg = await axios.get(`${API}/events/${r.data.event_id}/template`);
          if (cfg.data?.photos) {
            const tc = cfg.data;
            setTemplateConfig(tc);
            if (tc.bgColor) setBg(tc.bgColor);
            if (tc.activePreset) setTpl(tc.activePreset);
            if (tc.photos?.length > 0) {
              const p = tc.photos[0];
              setPhotoOrientation(p.h > p.w ? 'portrait' : 'landscape');
            }
            if (tc.orientation) setPhotoOrientation(tc.orientation);
          }
        } catch {
          // Fallback: try screen-config
          try {
            const cfg = await axios.get(`${API}/events/${r.data.event_id}/screen-config`);
            if (cfg.data?.template_config) {
              setTemplateConfig(cfg.data.template_config);
            }
          } catch { /* ignore */ }
        }
        const p = await axios.get(`${API}/events/${r.data.event_id}/photos`);
        setMom(p.data?.length || 0);
        setSls((p.data || []).filter(x => x.content_type?.startsWith('image/')).slice(0, 12));
      } catch (err) {
        // Don't immediately flip to "Event not found" — give the
        // recovery hook a chance to redeem a transient blip first.
        console.warn('[BoothPage] event load failed', err?.response?.status, err?.message);
      }
    })();
  }, [slug]);

  // Tiered recovery layer — surfaces ReconnectingScreen instead of the
  // harsh "Event not found" wall when the slug fetch hits a transient
  // network blip during browser-back navigation.
  const { event: recoveredEvent, status: recoveryStatus, retry: retryRecovery } = useEventRecovery(slug);
  const { activeEvent } = useEventSession();
  useEffect(() => {
    if (recoveryStatus === 'ready' && recoveredEvent?.event_id && !ev) {
      setEv(recoveredEvent);
    }
    if (recoveryStatus === 'failed' && !ev) setNotFound(true);
  }, [recoveryStatus, recoveredEvent, ev]);

  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices().then(d => {
      const c = d.filter(x => x.kind === 'videoinput'); setCams(c);
      if (c.length) setSelCam(c[0].deviceId);
    }).catch(() => {});
  }, [slug]);

  // ── Auto-fullscreen on first guest gesture ─────────────────
  // Matches the Booth OS pattern (BoothAtmosphereProvider): a single
  // capture-phase pointer/touch listener requests fullscreen the
  // moment the guest taps anywhere on the page. We honour their
  // intent if they exit (Esc / pull-down) via a sessionStorage flag
  // so we never re-summon the chrome retreat against them.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    if (!root?.requestFullscreen) return undefined;
    try {
      if (sessionStorage.getItem('booth_legacy_fs_attempted') === '1') return undefined;
    } catch { /* private mode */ }

    const onFirstGesture = () => {
      try { sessionStorage.setItem('booth_legacy_fs_attempted', '1'); } catch {}
      if (document.fullscreenElement) return;
      root.requestFullscreen({ navigationUI: 'hide' }).catch(() => {
        /* browser said no — silent no-op */
      });
    };
    const opts = { capture: true, once: true, passive: true };
    window.addEventListener('pointerdown', onFirstGesture, opts);
    window.addEventListener('touchstart', onFirstGesture, opts);
    return () => {
      window.removeEventListener('pointerdown', onFirstGesture, opts);
      window.removeEventListener('touchstart', onFirstGesture, opts);
    };
  }, []);

  useEffect(() => {
    if (scr !== 'attract' || !sls.length) return;
    const iv = setInterval(() => setSi(p => (p + 1) % sls.length), 3500);
    return () => clearInterval(iv);
  }, [scr, sls]);

  useEffect(() => {
    if (!ev) return;
    const iv = setInterval(async () => { try { const r = await axios.get(`${API}/events/${ev.event_id}/photos`); setMom(r.data?.length || 0); } catch {} }, 10000);
    return () => clearInterval(iv);
  }, [ev]);

  useEffect(() => () => { stopCam(); clearInterval(tmr.current); clearInterval(cdR.current); clearTimeout(rstR.current); }, []);

  const stopCam = useCallback(() => { if (strm.current) { strm.current.getTracks().forEach(t => t.stop()); strm.current = null; } setCam(false); }, []);

  const startCam = async (audio = false) => {
    try {
      // Match camera to photo orientation
      const isPortrait = photoOrientation === 'portrait';
      const camW = isPortrait ? 720 : 1280;
      const camH = isPortrait ? 1280 : 720;
      const s = await navigator.mediaDevices.getUserMedia({
        video: selCam
          ? { deviceId: { exact: selCam }, width: { ideal: camW }, height: { ideal: camH } }
          : { facingMode: 'user', width: { ideal: camW }, height: { ideal: camH } },
        audio
      });
      strm.current = s; setCam(true);
      requestAnimationFrame(() => { if (vRef.current) { vRef.current.srcObject = s; vRef.current.play().catch(() => {}); } });
      return true;
    } catch { return false; }
  };

  // Request browser fullscreen (hides address bar + taskbar)
  const enterFullscreen = () => {
    const el = document.documentElement;
    const rfs = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (rfs) rfs.call(el).catch(() => {});
  };

  const go = async (m) => {
    enterFullscreen();
    setMode(m); setPics([]); setVidUrl(null); setVidBlob(null); setBi(0); setQr(null); setComp(null); setBetweenText('');
    const ok = await startCam(m === 'video');
    if (!ok) return;
    // Step 1: Switch to a blank camera screen (no numbers, no text)
    setScr('waiting');
    // Step 2: After camera settles, show GET READY
    setTimeout(() => {
      say('get-ready'); setBetweenText('GET READY');
      // Step 3: Clear GET READY, pause, then start countdown
      setTimeout(() => {
        setBetweenText('');
        setTimeout(() => {
          runCD(() => { if (m === 'photo') { say('smile'); setTimeout(() => snap(0, []), 300); } else startVid(); });
        }, 800);
      }, 3000);
    }, 1000);
  };

  const runCD = (cb) => {
    setBetweenText(''); // Clear any overlay text before countdown
    setScr('countdown'); setCd(3); say('three');
    let c = 3;
    cdR.current = setInterval(() => { c--; setCd(c); if (c===2) say('two'); if (c===1) say('one'); if (c<=0) { clearInterval(cdR.current); cb(); } }, 1000);
  };

  const snap = (i, arr) => {
    setBi(i); setScr('capture'); setBetweenText('');
    setFl(true); shutter(); setTimeout(() => setFl(false), 180);
    if (vRef.current && c1.current) {
      const cv = c1.current, v = vRef.current;
      cv.width = v.videoWidth || 1280; cv.height = v.videoHeight || 720;
      const ctx = cv.getContext('2d'); ctx.save(); ctx.scale(-1,1);
      ctx.drawImage(v, -cv.width, 0, cv.width, cv.height); ctx.restore();
      cv.toBlob(blob => {
        const url = blob ? URL.createObjectURL(blob) : null;
        const next = blob ? [...arr, { blob, url }] : arr;
        setPics(next);
        if (i < 2) {
          const voice = i === 0 ? 'one-more' : 'last-one';
          const label = i === 0 ? 'One more!' : 'Last one!';
          setTimeout(() => { say(voice); setBetweenText(label); setTimeout(() => { setBetweenText(''); runCD(() => { say('smile'); setTimeout(() => snap(i+1, next), 300); }); }, 1200); }, 4500);
        } else {
          // 3rd photo taken — show it in thumbnails, wait 1.5s, then move to result
          setTimeout(() => { stopCam(); compose(next); }, 1500);
        }
      }, 'image/jpeg', 0.92);
    } else if (i < 2) setTimeout(() => snap(i+1, arr), 7000);
    else { stopCam(); compose(arr); }
  };

  const compose = async (p) => {
    if (!p.length) { setScr('attract'); return; }
    setScr('result'); say('creating-qr'); setUpl(true);
    const blob = await new Promise(resolve => {
      const cv = c2.current || document.createElement('canvas');

      // Use saved template config if available
      if (templateConfig && templateConfig.photos?.length > 0) {
        const tc = templateConfig;
        // Use saved canvas dimensions, or calculate from elements
        cv.width = tc.canvasW || 400;
        cv.height = tc.canvasH || 600;
        const ctx = cv.getContext('2d');

        // Background
        if (tc.bgImage) {
          const bgImg = new Image();
          bgImg.crossOrigin = 'anonymous';
          bgImg.onload = () => { ctx.drawImage(bgImg, 0, 0, cv.width, cv.height); drawTemplateContent(ctx, cv, tc, p, resolve); };
          bgImg.onerror = () => { ctx.fillStyle = tc.bgColor || '#FFFFFF'; ctx.fillRect(0, 0, cv.width, cv.height); drawTemplateContent(ctx, cv, tc, p, resolve); };
          bgImg.src = tc.bgImage;
        } else {
          ctx.fillStyle = tc.bgColor || '#FFFFFF';
          ctx.fillRect(0, 0, cv.width, cv.height);
          drawTemplateContent(ctx, cv, tc, p, resolve);
        }
      } else {
        // Fallback: simple strip
        const pad = 40, pw = 400, ph = 300;
        cv.width = pw + pad * 2; cv.height = pad + (ph + pad) * p.length + 60;
        const ctx = cv.getContext('2d'); ctx.fillStyle = bg; ctx.fillRect(0, 0, cv.width, cv.height);
        let ld = 0;
        p.forEach((pic, idx) => {
          const img = new Image(); img.onload = () => {
            const x = pad, y = pad + idx * (ph + pad);
            ctx.save(); roundClip(ctx, x, y, pw, ph, 16); coverDraw(ctx, img, x, y, pw, ph); ctx.restore();
            ld++; if (ld === p.length) { ctx.fillStyle = '#000'; ctx.font = 'bold 22px Inter, sans-serif'; ctx.textAlign = 'center'; ctx.fillText(ev?.event_name || 'EventDrop', cv.width/2, cv.height - 25); drawPoweredBy(ctx, cv); cv.toBlob(b => resolve(b), 'image/jpeg', 0.92); }
          }; img.onerror = () => { ld++; if (ld === p.length) { drawPoweredBy(ctx, cv); cv.toBlob(b => resolve(b), 'image/jpeg', 0.92); } }; img.src = pic.url;
        });
      }
    });
    if (blob) {
      setComp(URL.createObjectURL(blob));
      try { const f = new File([blob], `booth_${tpl}_${Date.now()}.jpg`, { type: 'image/jpeg' }); const fd = new FormData(); fd.append('file', f); const r = await axios.post(`${API}/events/${ev.event_id}/photos?source=booth`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); if (r.data?.photo_id) setQr(`${window.location.origin}/download/${r.data.photo_id}`); } catch { setQr(`${window.location.origin}/event/${slug}/guest`); }
    }
    for (const pic of p) { try { const f = new File([pic.blob], `booth_photo_${Date.now()}.jpg`, { type: 'image/jpeg' }); const fd = new FormData(); fd.append('file', f); await axios.post(`${API}/events/${ev.event_id}/photos?source=booth`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch {} }
    if (!qr) setQr(`${window.location.origin}/event/${slug}/guest`);
    setMom(prev => prev + p.length + 1); setUpl(false);
    setTimeout(() => say('thank-you'), 1000);
    rstR.current = setTimeout(() => done(), 120000);
  };

  // ── Guest template override ──
  // Built-in preset layouts guests can pick from on the result screen. These override
  // whatever template the host saved in the editor — gives guests quick stylistic choice
  // without needing the full editor. Canvas is 400x600 portrait.
  const GUEST_PRESETS = {
    strip: { w: 400, h: 600, photos: [{x:20,y:20,w:360,h:170},{x:20,y:205,w:360,h:170},{x:20,y:390,w:360,h:170}], nameY: 585 },
    grid: { w: 400, h: 600, photos: [{x:20,y:20,w:175,h:260},{x:205,y:20,w:175,h:260},{x:110,y:295,w:180,h:260}], nameY: 585 },
    polaroid: { w: 400, h: 520, photos: [{x:30,y:30,w:340,h:340}], nameY: 440 },
    fullframe: { w: 400, h: 600, photos: [{x:0,y:0,w:400,h:600}], nameY: 575, nameColor: '#fff' },
  };

  const [remaking, setRemaking] = useState(false);

  const remakeWithPreset = async (presetId) => {
    if (!pics.length || remaking) return;
    setRemaking(true);
    const preset = GUEST_PRESETS[presetId];
    const blob = await new Promise(resolve => {
      const cv = c2.current || document.createElement('canvas');
      cv.width = preset.w; cv.height = preset.h;
      const ctx = cv.getContext('2d');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, cv.width, cv.height);
      // Polaroid uses a white card look
      if (presetId === 'polaroid') {
        ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, cv.width, cv.height);
        ctx.shadowColor = 'rgba(0,0,0,0.1)'; ctx.shadowBlur = 12; ctx.fillRect(15, 15, cv.width - 30, cv.height - 30);
        ctx.shadowBlur = 0;
      }
      let loaded = 0;
      const slots = preset.photos;
      const n = Math.min(slots.length, pics.length);
      if (n === 0) { drawPoweredBy(ctx, cv); cv.toBlob(b => resolve(b), 'image/jpeg', 0.92); return; }
      for (let i = 0; i < n; i++) {
        const slot = slots[i];
        const pic = pics[i];
        const img = new Image();
        img.onload = () => {
          ctx.save();
          const radius = presetId === 'fullframe' ? 0 : 14;
          if (radius) roundClip(ctx, slot.x, slot.y, slot.w, slot.h, radius);
          coverDraw(ctx, img, slot.x, slot.y, slot.w, slot.h);
          ctx.restore();
          loaded++;
          if (loaded === n) {
            // Event name
            ctx.fillStyle = preset.nameColor || (presetId === 'polaroid' ? '#333' : '#fff');
            ctx.font = 'bold 18px Inter, system-ui, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(ev?.event_name || 'EventDrop', cv.width / 2, preset.nameY);
            drawPoweredBy(ctx, cv);
            cv.toBlob(b => resolve(b), 'image/jpeg', 0.92);
          }
        };
        img.onerror = () => { loaded++; if (loaded === n) { drawPoweredBy(ctx, cv); cv.toBlob(b => resolve(b), 'image/jpeg', 0.92); } };
        img.src = pic.url;
      }
    });
    if (blob) {
      if (comp) URL.revokeObjectURL(comp);
      setComp(URL.createObjectURL(blob));
      try {
        const f = new File([blob], `booth_${presetId}_${Date.now()}.jpg`, { type: 'image/jpeg' });
        const fd = new FormData();
        fd.append('file', f);
        const r = await axios.post(`${API}/events/${ev.event_id}/photos?source=booth`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (r.data?.photo_id) setQr(`${window.location.origin}/download/${r.data.photo_id}`);
      } catch {}
    }
    setRemaking(false);
  };

  // Draw photos + texts from saved template config
  const drawTemplateContent = (ctx, cv, tc, capturedPics, resolve) => {
    const photoSlots = tc.photos || [];
    const textBlocks = tc.texts || [];
    let loaded = 0;
    const totalPhotos = Math.min(photoSlots.length, capturedPics.length);

    if (totalPhotos === 0) {
      // No photo slots — just draw texts
      drawTexts(ctx, textBlocks);
      drawPoweredBy(ctx, cv);
      cv.toBlob(b => resolve(b), 'image/jpeg', 0.92);
      return;
    }

    for (let i = 0; i < totalPhotos; i++) {
      const slot = photoSlots[i];
      const pic = capturedPics[i];
      const img = new Image();
      img.onload = () => {
        ctx.save();
        roundClip(ctx, slot.x, slot.y, slot.w, slot.h, 12);
        coverDraw(ctx, img, slot.x, slot.y, slot.w, slot.h);
        ctx.restore();
        loaded++;
        if (loaded === totalPhotos) {
          drawTexts(ctx, textBlocks);
          drawPoweredBy(ctx, cv);
          cv.toBlob(b => resolve(b), 'image/jpeg', 0.92);
        }
      };
      img.onerror = () => {
        loaded++;
        if (loaded === totalPhotos) {
          drawTexts(ctx, textBlocks);
          drawPoweredBy(ctx, cv);
          cv.toBlob(b => resolve(b), 'image/jpeg', 0.92);
        }
      };
      img.src = pic.url;
    }
  };

  const drawTexts = (ctx, textBlocks) => {
    textBlocks.forEach(t => {
      ctx.fillStyle = t.color || '#000000';
      ctx.font = `bold ${t.fontSize || 20}px ${t.fontFamily || 'Inter'}, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(t.text || '', t.x || 0, t.y || 0);
    });
  };

  const startVid = () => {
    setScr('capture'); setVt(0); chnk.current = [];
    const s = strm.current; if (!s) { setScr('attract'); return; }
    const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';
    const r = new MediaRecorder(s, { mimeType: mime }); rec.current = r;
    r.ondataavailable = e => { if (e.data.size > 0) chnk.current.push(e.data); };
    r.onstop = () => { const b = new Blob(chnk.current, { type: mime }); stopCam(); setVidBlob(b); setVidUrl(URL.createObjectURL(b)); vidDone(b); };
    r.start(100); let t = 0; tmr.current = setInterval(() => { t++; setVt(t); if (t >= 15) stopVid(); }, 1000);
  };
  const stopVid = () => { clearInterval(tmr.current); if (rec.current?.state !== 'inactive') rec.current.stop(); };

  const vidDone = async (blob) => {
    setScr('result'); say('downloading'); setUpl(true);
    try { const f = new File([blob], `booth_video_${Date.now()}.webm`, { type: 'video/webm' }); const fd = new FormData(); fd.append('file', f); const r = await axios.post(`${API}/events/${ev.event_id}/photos?source=booth`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setMom(prev => prev + 1); if (r.data?.photo_id) setQr(`${window.location.origin}/download/${r.data.photo_id}`); else setQr(`${window.location.origin}/event/${slug}/guest`); } catch { setQr(`${window.location.origin}/event/${slug}/guest`); }
    setUpl(false); setTimeout(() => { say('creating-qr'); setTimeout(() => say('thank-you'), 2000); }, 500);
    rstR.current = setTimeout(() => done(), 120000);
  };

  const done = () => { clearTimeout(rstR.current); pics.forEach(p => { if (p.url) URL.revokeObjectURL(p.url); }); if (vidUrl) URL.revokeObjectURL(vidUrl); if (comp) URL.revokeObjectURL(comp); setPics([]); setVidBlob(null); setVidUrl(null); setQr(null); setComp(null); setBetweenText(''); setScr('attract'); };

  const tapA = () => { const t = taps + 1; setTaps(t); if (t >= 5) { setAdmin(true); setTaps(0); } setTimeout(() => setTaps(0), 2000); };

  const slBg = () => { if (!sls.length) return {}; const p = sls[si]; return p?.storage_path ? { backgroundImage: `url(${getMediaUrl(p.storage_path, `${BACKEND_URL}/api/photos/${p.storage_path}`)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}; };

  // Canvas helpers
  const roundClip = (ctx, x, y, w, h, r) => { ctx.beginPath(); ctx.moveTo(x+r,y); ctx.lineTo(x+w-r,y); ctx.quadraticCurveTo(x+w,y,x+w,y+r); ctx.lineTo(x+w,y+h-r); ctx.quadraticCurveTo(x+w,y+h,x+w-r,y+h); ctx.lineTo(x+r,y+h); ctx.quadraticCurveTo(x,y+h,x,y+h-r); ctx.lineTo(x,y+r); ctx.quadraticCurveTo(x,y,x+r,y); ctx.closePath(); ctx.clip(); };
  const coverDraw = (ctx, img, x, y, w, h) => { const ir=img.width/img.height, sr=w/h; let sx=0,sy=0,sw=img.width,sh=img.height; if(ir>sr){sw=img.height*sr;sx=(img.width-sw)/2;}else{sh=img.width/sr;sy=(img.height-sh)/2;} ctx.drawImage(img,sx,sy,sw,sh,x,y,w,h); };
  const roundRect = (ctx, x, y, w, h, r) => { ctx.beginPath(); ctx.moveTo(x+r,y); ctx.lineTo(x+w-r,y); ctx.quadraticCurveTo(x+w,y,x+w,y+r); ctx.lineTo(x+w,y+h-r); ctx.quadraticCurveTo(x+w,y+h,x+w-r,y+h); ctx.lineTo(x+r,y+h); ctx.quadraticCurveTo(x,y+h,x,y+h-r); ctx.lineTo(x,y+r); ctx.quadraticCurveTo(x,y,x+r,y); ctx.closePath(); ctx.fill(); };

  // Always-on, non-removable "Powered by EventDrop.co" footer. Drawn last so it sits on top.
  // Hidden when the host has purchased the Pro Upgrade (watermark_hidden flag in template_config).
  const drawPoweredBy = (ctx, cv) => {
    // Read the watermark flag from the latest template_config snapshot
    const hide = !!(templateConfigRef.current && templateConfigRef.current.watermark_hidden);
    if (hide) return;
    const text = 'Powered by EventDrop.co';
    ctx.save();
    ctx.font = '600 11px Inter, system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    const pad = 8;
    const metrics = ctx.measureText(text);
    const tw = metrics.width + pad * 2;
    const th = 20;
    const x = cv.width - 8;
    const y = cv.height - 8;
    // Pill background
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    roundRect(ctx, x - tw, y - th, tw, th, 10);
    // Text
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fillText(text, x - pad, y - 5);
    ctx.restore();
  };

  if (notFound) return <ReconnectingScreen status="failed" onRetry={() => { setNotFound(false); retryRecovery(); }} dashboardHref="/" slug={slug} activeEvent={activeEvent} />;
  if (!ev) return <ReconnectingScreen status={recoveryStatus === 'restoring' ? 'restoring' : 'reconnecting'} slug={slug} activeEvent={activeEvent} />;

  return (
    <div className="fixed inset-0 overflow-hidden select-none" style={{ background: 'radial-gradient(circle at center, #15161B, #000)', cursor: scr !== 'attract' && !admin ? 'none' : 'default' }} data-testid="booth-page">
      {/* Universal back-nav — hidden during active capture/countdown so
          the kiosk operator can't be bumped mid-shot. */}
      {!['countdown', 'capture', 'review'].includes(scr) && (
        <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 18, left: 18, zIndex: 60, paddingTop: 'env(safe-area-inset-top)' }}>
          <BackButton fallbackHref={slug ? `/event/${slug}` : '/'} label="Back" variant="subtle" testId="booth-back-btn" />
        </div>
      )}
      <canvas ref={c1} className="hidden" /><canvas ref={c2} className="hidden" />
      <video ref={vRef} autoPlay playsInline muted className="fixed inset-0 w-full h-full object-cover" style={{ transform:'scaleX(-1)', zIndex: cam && ['countdown','capture','waiting'].includes(scr) ? 5 : -1, opacity: cam && ['countdown','capture','waiting'].includes(scr) ? 1 : 0 }} />
      {fl && <div className="fixed inset-0 z-50 bg-white pointer-events-none" style={{ animation:'bFlash .18s ease-out' }} />}

      {/* Between-shot text overlay */}
      {betweenText && <div className="fixed inset-0 z-30 flex items-center justify-center pointer-events-none" style={{ animation:'bFade .3s ease-out' }}><span className="text-5xl font-black text-white" style={{ textShadow:'0 0 40px rgba(62,198,231,0.5)' }}>{betweenText}</span></div>}

      {/* ═══ ATTRACT ═══ */}
      {scr === 'attract' && (
        <div className="w-full h-full flex flex-col items-center justify-center relative" style={{ animation:'bIn .5s ease-out' }} onClick={enterFullscreen} data-testid="booth-attract">
          <div className="absolute inset-0 transition-all duration-1000" style={{ ...slBg(), filter:'blur(14px) brightness(0.2) saturate(1.3)' }} />
          <div className="absolute inset-0" style={{ background:'radial-gradient(circle at center, transparent 20%, rgba(0,0,0,0.7) 100%)' }} />
          <div className="relative z-10 flex flex-col items-center px-6 w-full max-w-2xl">
            <EventDropLogo size={56} className="mb-6 opacity-70" />
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white text-center mb-4 tracking-tight" style={{ textShadow:'0 4px 30px rgba(0,0,0,0.6)' }} data-testid="booth-event-name">
              {ev.event_name || "Tonight's Event"}
            </h1>
            <p className="text-white/30 text-xl mb-14">Choose your moment</p>

            <div className="flex gap-6 w-full justify-center mb-12">
              <button onClick={() => go('photo')} className="booth-card group" style={{ minHeight: 72 }} data-testid="booth-photo-start">
                <div className="w-32 h-32 rounded-[26px] flex items-center justify-center mb-4 transition-transform group-hover:scale-110 group-active:scale-95" style={{ background:'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)', boxShadow:'0 18px 50px rgba(184,151,98,0.5), 0 0 0 1px rgba(255,255,255,0.08) inset' }}>
                  <Camera size={64} weight="bold" className="text-white" />
                </div>
                <span className="text-white font-black" style={{ fontSize: 22, letterSpacing: '-0.02em' }}>Photo Booth</span>
                <span className="text-white/55 mt-1" style={{ fontSize: 14, fontWeight: 600 }}>3 photos</span>
              </button>
              <button onClick={() => go('video')} className="booth-card group" style={{ minHeight: 72 }} data-testid="booth-video-start">
                <div className="w-32 h-32 rounded-[26px] flex items-center justify-center mb-4 transition-transform group-hover:scale-110 group-active:scale-95" style={{ background:'linear-gradient(135deg, #FF3B5C 0%, #FF0099 100%)', boxShadow:'0 18px 50px rgba(255,59,92,0.5), 0 0 0 1px rgba(255,255,255,0.08) inset' }}>
                  <VideoCamera size={64} weight="bold" className="text-white" />
                </div>
                <span className="text-white font-black" style={{ fontSize: 22, letterSpacing: '-0.02em' }}>Video Booth</span>
                <span className="text-white/55 mt-1" style={{ fontSize: 14, fontWeight: 600 }}>15 seconds</span>
              </button>
            </div>

            <div className="px-8 py-4 rounded-full" style={{ background:'rgba(255,255,255,0.06)', backdropFilter:'blur(24px)', border:'1px solid rgba(255,255,255,0.1)' }} data-testid="booth-moment-counter">
              <span className="text-[#B89762] font-bold text-2xl">{mom}</span>
              <span className="text-white/35 text-base ml-2">moments captured tonight</span>
            </div>
            <button onClick={tapA} className="absolute bottom-3 right-3 w-10 h-10 rounded-full opacity-0" />
          </div>
        </div>
      )}

      {/* ═══ WAITING (camera + GET READY overlay, no numbers) ═══ */}
      {scr === 'waiting' && (
        <div className="w-full h-full relative" style={{ zIndex:10 }} data-testid="booth-waiting" />
      )}

      {/* ═══ COUNTDOWN ═══ */}
      {scr === 'countdown' && (
        <div className="w-full h-full relative" style={{ zIndex:10 }}>
          {/* Photo strip on left during capture */}
          {mode === 'photo' && pics.length > 0 && (
            <div className="absolute top-6 left-4 flex flex-col gap-2 z-20" data-testid="booth-strip-preview">
              {pics.map((p, i) => (
                <div key={i} className="w-16 h-12 rounded-lg overflow-hidden border border-white/30" style={{ boxShadow:'0 4px 12px rgba(0,0,0,0.5)', animation:'bSlide .4s ease-out' }}>
                  <img src={p.url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div key={`${cd}-${bi}`} style={{ animation:'bPop .6s ease-out' }}>
              <span className="text-[10rem] font-black text-white leading-none" style={{ textShadow:'0 0 80px rgba(62,198,231,0.35)' }}>{cd}</span>
            </div>
          </div>
          <div className="absolute bottom-10 left-0 right-0 text-center z-10">
            <span className="px-6 py-2.5 rounded-full text-white/50 text-sm font-semibold" style={{ background:'rgba(0,0,0,0.4)', backdropFilter:'blur(12px)' }}>
              {mode === 'photo' ? `Photo ${bi + 1} of 3` : 'Video'}
            </span>
          </div>
        </div>
      )}

      {/* ═══ CAPTURE ═══ */}
      {scr === 'capture' && mode === 'photo' && (
        <div className="w-full h-full relative" style={{ zIndex:10 }}>
          {pics.length > 0 && (
            <div className="absolute top-6 left-4 flex flex-col gap-2 z-20">
              {pics.map((p, i) => (
                <div key={i} className="w-16 h-12 rounded-lg overflow-hidden border border-white/30" style={{ boxShadow:'0 4px 12px rgba(0,0,0,0.5)', animation:'bSlide .4s ease-out' }}>
                  <img src={p.url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
          <div className="absolute top-6 right-6 flex items-center gap-2 px-4 py-2 rounded-full z-20" style={{ background:'rgba(62,198,231,0.85)', backdropFilter:'blur(8px)' }}>
            <div className="w-2.5 h-2.5 rounded-full bg-white" />
            <span className="text-white text-sm font-bold">Photo {bi + 1} of 3</span>
          </div>
        </div>
      )}
      {scr === 'capture' && mode === 'video' && (
        <div className="w-full h-full relative" style={{ zIndex:10 }}>
          {/* Top status — Event Visibility: large REC pip + 20px timer pill, top-centre */}
          <div className="absolute z-20 flex items-center gap-3 px-5 py-2.5 rounded-full" style={{ top: 'max(20px, env(safe-area-inset-top))', left: '50%', transform: 'translateX(-50%)', background:'rgba(0,0,0,0.7)', backdropFilter:'blur(14px)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
            <span style={{ width: 16, height: 16, borderRadius: 999, background: '#FF3B5C', boxShadow: '0 0 18px rgba(255,59,92,0.85), 0 0 4px rgba(255,255,255,0.3) inset', animation: 'boothRecPulse 1.1s ease-in-out infinite' }} />
            <span className="text-white tabular-nums" style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em' }}>{vt}s</span>
            <span className="text-white/55" style={{ fontSize: 14, fontWeight: 700 }}>/ 15s</span>
          </div>
          <div className="absolute left-0 right-0 flex justify-center z-20" style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 48px)' }}>
            <button onClick={stopVid} className="transition-all active:scale-95" style={{ width: 96, height: 96, borderRadius: '50%', border: '5px solid rgba(255,255,255,1)', background: 'linear-gradient(135deg, #FF3B5C 0%, #FF0099 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow:'0 16px 40px rgba(255,59,92,0.55), 0 0 0 1px rgba(0,0,0,0.4)' }} data-testid="booth-stop-video">
              <div style={{ width: 36, height: 36, borderRadius: 8, background: '#FFFFFF' }} />
            </button>
          </div>
        </div>
      )}

      {/* ═══ RESULT ═══ */}
      {scr === 'result' && (
        <div className="w-full h-full flex flex-col items-center justify-center px-6" style={{ animation:'bIn .5s ease-out', background:'radial-gradient(circle at center, #15161B, #000)' }} data-testid="booth-result">

          {/* Template / Video — BIG */}
          {comp ? (
            <div className="rounded-[28px] overflow-hidden mb-4" style={{ maxWidth:'85vw', maxHeight:'50vh', boxShadow:'0 25px 80px rgba(0,0,0,0.5)' }} data-testid="booth-composite">
              <img src={comp} alt="Photo strip" className="w-auto h-auto object-contain" style={{ maxWidth:'85vw', maxHeight:'50vh' }} />
            </div>
          ) : vidUrl ? (
            <div className="rounded-[28px] overflow-hidden mb-6" style={{ width:'75vw', maxWidth:640, boxShadow:'0 25px 80px rgba(0,0,0,0.5)' }}>
              <video src={vidUrl} autoPlay loop muted className="w-full aspect-video object-cover" />
            </div>
          ) : null}

          {/* Guest template picker — lets the guest pick their own layout after capture */}
          {comp && pics.length > 0 && !upl && (
            <div className="mb-4 flex flex-col items-center" data-testid="booth-template-picker">
              <p className="text-white/50 text-xs mb-2 font-semibold uppercase tracking-wider">Try a different layout</p>
              <div className="flex gap-2">
                {[
                  { id: 'strip', label: 'Strip' },
                  { id: 'grid', label: 'Grid' },
                  { id: 'polaroid', label: 'Polaroid' },
                  { id: 'fullframe', label: 'Full' },
                ].map(p => (
                  <button key={p.id}
                    onClick={() => remakeWithPreset(p.id)}
                    disabled={remaking}
                    className="px-4 py-2 rounded-full text-xs font-bold transition-all hover:scale-105 active:scale-95 disabled:opacity-40"
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', backdropFilter: 'blur(20px)' }}
                    data-testid={`booth-preset-${p.id}`}>
                    {p.label}
                  </button>
                ))}
              </div>
              {remaking && <p className="text-white/40 text-xs mt-2">Re-creating…</p>}
            </div>
          )}

          {/* Film seed */}
          <p className="text-white/25 text-sm mb-5" style={{ animation:'bSeed 1s ease-out .5s both' }}>This moment will be part of your event film</p>

          {/* QR */}
          {upl ? (
            <div className="flex items-center gap-3 mb-5"><div className="animate-spin rounded-full h-8 w-8 border-3 border-[#B89762]/50 border-t-transparent"></div><span className="text-white/30 text-sm">Preparing...</span></div>
          ) : qr ? (
            <div className="flex flex-col items-center mb-5" style={{ animation:'bQr .4s ease-out' }}>
              <p className="text-white text-lg font-semibold mb-3">Your photo is ready</p>
              <div className="rounded-[20px] p-5" style={{ background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.15)', backdropFilter:'blur(20px)', boxShadow:'0 12px 40px rgba(0,0,0,0.3)' }} data-testid="booth-qr">
                <QRCodeSVG value={qr} size={160} bgColor="transparent" fgColor="#FFFFFF" />
              </div>
              <p className="text-white/35 text-sm mt-3">Scan to download & share</p>
            </div>
          ) : null}

          {/* Done */}
          <div className="flex flex-col items-center gap-2">
            <p className="text-white/15 text-xs">Scan your photo, then tap below</p>
            <button onClick={done} className="transition-all active:scale-95" style={{ minHeight: 72, padding: '0 44px', borderRadius: 999, background:'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)', color: '#FFFFFF', fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', border: 'none', boxShadow:'0 18px 50px rgba(184,151,98,0.5), 0 0 0 1px rgba(255,255,255,0.08) inset', cursor: 'pointer' }} data-testid="booth-done-btn">
              Let's Go Again!
            </button>
          </div>
        </div>
      )}

      {/* ═══ ADMIN ═══ */}
      {admin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background:'rgba(0,0,0,0.88)', backdropFilter:'blur(10px)' }} onClick={() => setAdmin(false)}>
          <div className="bg-white rounded-[28px] p-8 max-w-sm w-full" style={{ boxShadow:'0 30px 80px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()} data-testid="booth-settings">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-bold text-xl text-gray-900">Booth Settings</h3>
              <button onClick={() => setAdmin(false)} className="p-2 rounded-full hover:bg-gray-100"><X size={20} weight="bold" /></button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Camera</label>
                <select value={selCam} onChange={e => setSelCam(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm" data-testid="booth-camera-select">
                  {cams.map(c => <option key={c.deviceId} value={c.deviceId}>{c.label || `Camera ${cams.indexOf(c)+1}`}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Template Background</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={bg} onChange={e => { setBg(e.target.value); axios.put(`${API}/events/${ev.event_id}/screen-config`, { booth_bg_color: e.target.value }).catch(() => {}); }} className="w-10 h-10 rounded-xl border-0 cursor-pointer" data-testid="booth-bg-color" />
                  <div className="flex gap-2 ml-auto">
                    {['#15161B','#15161B','#1A1A2E','#2D1B2E','#FFFFFF'].map(c => (
                      <button key={c} onClick={() => { setBg(c); axios.put(`${API}/events/${ev.event_id}/screen-config`, { booth_bg_color: c }).catch(() => {}); }}
                        className="w-8 h-8 rounded-full border-2 transition-all hover:scale-110" style={{ background:c, borderColor: bg===c ? '#C29D52' : '#E5E7EB' }} />
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Template Style</label>
                <div className="grid grid-cols-3 gap-2">
                  {[{ id:'strip', label:'Strip' },{ id:'grid', label:'Grid' },{ id:'polaroid', label:'Polaroid' }].map(t => (
                    <button key={t.id} onClick={() => { setTpl(t.id); axios.put(`${API}/events/${ev.event_id}/screen-config`, { booth_template_style: t.id }).catch(() => {}); }}
                      className={`py-3 rounded-xl text-sm font-semibold transition-all ${tpl===t.id ? 'bg-[#B89762] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`tpl-${t.id}`}>{t.label}</button>
                  ))}
                </div>
              </div>
              <div className="p-4 rounded-xl bg-gray-50"><p className="font-semibold text-gray-900 text-sm">{ev.event_name}</p></div>
              <button onClick={() => { setAdmin(false); window.location.href = `/event/${slug}/booth/editor`; }}
                className="w-full py-3 rounded-xl font-bold text-white text-sm transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }} data-testid="open-editor-btn">
                Open Template Editor
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes bIn { from{opacity:0} to{opacity:1} }
        @keyframes bFlash { from{opacity:.85} to{opacity:0} }
        @keyframes bPop { from{transform:scale(3);opacity:0} 25%{transform:scale(.85);opacity:1} to{transform:scale(1)} }
        @keyframes bSeed { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes bQr { from{opacity:0;transform:scale(.9)} to{opacity:1;transform:scale(1)} }
        @keyframes boothRecPulse {
          0%, 100% { box-shadow: 0 0 18px rgba(255,59,92,0.85), 0 0 4px rgba(255,255,255,0.3) inset; transform: scale(1); }
          50% { box-shadow: 0 0 28px rgba(255,59,92,1), 0 0 4px rgba(255,255,255,0.3) inset; transform: scale(1.12); }
        }
        @keyframes bSlide { from{opacity:0;transform:translateX(-20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes bFade { from{opacity:0} to{opacity:1} }
        .booth-card { display:flex; flex-direction:column; align-items:center; padding:36px 44px; border-radius:28px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); backdrop-filter:blur(24px); transition:all .25s ease; cursor:pointer; }
        .booth-card:hover { background:rgba(255,255,255,0.09); border-color:rgba(255,255,255,0.18); transform:translateY(-4px); }
        .booth-card:active { transform:scale(.97); }
      `}</style>
    </div>
  );
};

export default BoothPage;
